import React, { useEffect, useState } from "react";
import "./ManageFolderTile.css";

import FolderForm from "../../components/FolderForm";
import FileUpload from "../../components/FileUpload";
import LinkUpload from "../../components/LinkUpload";

import { FiFolderPlus, FiUploadCloud, FiLink } from "react-icons/fi";

export default function ManageFolderTile({
  folderId,
  currentPath,
  onSuccess,
  canManage = false,

  // layout
  variant = "tile", // "tile" | "inline"
  showTitle = true, // ignored when variant="inline"
  controlsOnly = false, // when true: render only the 3 pills (no panel/body)

  // controlled mode (optional)
  active, // 'create' | 'upload' | 'link' | null
  onActiveChange, // (next) => void
}) {
  // ---- DO NOT early-return before hooks ----
  const isControlled = typeof active !== "undefined";
  const [internalActive, setInternalActive] = useState(null);

  // current value
  const curr = isControlled ? active : internalActive;

  // safe setter: works for controlled & uncontrolled
  const setCurr = (nextOrUpdater) => {
    if (isControlled) {
      const next =
        typeof nextOrUpdater === "function"
          ? nextOrUpdater(curr)
          : nextOrUpdater;
      onActiveChange?.(next);
    } else {
      setInternalActive(nextOrUpdater);
    }
  };

  // close when folder changes (so it never stays open after navigation)
  useEffect(() => {
    if (!isControlled) setInternalActive(null);
  }, [folderId, currentPath, isControlled]);

  const handleSuccess = () => {
    onSuccess?.();
    setCurr(null);
  };

  const isInline = variant === "inline";
  const toggle = (key) => setCurr((prev) => (prev === key ? null : key));

  // It’s safe to return early here (AFTER hooks have been called)
  if (!canManage) return null;

  return (
    <div className={`manage-tile ${isInline ? "inline" : ""}`}>
      <div className="manage-tile-header">
        {!isInline && showTitle && (
          <h4 className="manage-title">Manage This Folder</h4>
        )}
        <div className="manage-actions">
          <button
            type="button"
            className={`premium-pill ${curr === "create" ? "active" : ""}`}
            onClick={() => toggle("create")}
            title="Create a subfolder"
          >
            <FiFolderPlus className="icon" />
            <span className="label">Create Folder</span>
          </button>
          <button
            type="button"
            className={`premium-pill ${curr === "upload" ? "active" : ""}`}
            onClick={() => toggle("upload")}
            title="Upload files into this folder"
          >
            <FiUploadCloud className="icon" />
            <span className="label">Upload File</span>
          </button>
          <button
            type="button"
            className={`premium-pill ${curr === "link" ? "active" : ""}`}
            onClick={() => toggle("link")}
            title="Add a link entry"
          >
            <FiLink className="icon" />
            <span className="label">Add Link</span>
          </button>
        </div>
      </div>

      {/* When inline + controlsOnly = true, render only the pills (panel handled by parent) */}
      {controlsOnly ? null : (
        <div className="manage-body">
          {curr === "create" && (
            <div className="manage-panel">
              <FolderForm
                parentId={folderId}
                onSuccess={handleSuccess}
              />
            </div>
          )}
          {curr === "upload" && (
            <div className="manage-panel">
              <FileUpload
                folderId={folderId}
                onSuccess={handleSuccess}
              />
            </div>
          )}
          {curr === "link" && (
            <div className="manage-panel">
              <LinkUpload
                folderId={folderId}
                onSuccess={handleSuccess}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
